import { useEffect } from 'react'
import ChevronRight from '@untitled-ui/icons-react/build/esm/ChevronRight'
import { ProgressBar, StatusDot } from '@clickguard/design-system'
import { useAccountStore, useUserStore } from 'store'
import { useShallow } from 'zustand/react/shallow'
import { formatUsageSpend } from 'utils/billing-display'
import { useNavigate } from 'react-router-dom'
import { ProductAccounts } from 'services/user'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'
import moment from 'moment'
import { useProtectionContext } from 'hooks/use-protection-context'
import { BoostTag } from 'components/black-friday-banner/boost-tag'

interface UsageCardProps {
	onExtendProtection: () => void
}

const getNextSpendResetDate = (
	nextBillingDate: Date | string | null | undefined,
	endsAt: Date | string | null | undefined,
	endedAt: Date | string | null | undefined
): moment.Moment => {
	const billingDate = nextBillingDate || endsAt || endedAt
	if (!billingDate) return moment()

	const billingDay = moment(billingDate).date()
	const now = moment()
	let resetDate = moment().date(billingDay)

	if (now.isSameOrAfter(resetDate, 'day')) {
		resetDate = resetDate.add(1, 'month')
	}

	return resetDate
}

const getProgressPercentage = (
	current: number,
	limit: number | null | undefined
) => {
	if (!limit || limit === 0) return 0
	return Math.min((current / limit) * 100, 100)
}

const getProgressVariant = (percentage: number) => {
	if (percentage >= 90) return 'danger'
	return 'brand'
}

const getSpendColorClass = (percentage: number) => {
	if (percentage >= 90) return 'cg-text-red-500'
	return 'cg-text-brand'
}

export const UsageCard = ({ onExtendProtection }: UsageCardProps) => {
	const navigate = useNavigate()
	const userId = useUserStore((state) => state.user?.id)
	const trackPostHogEvent = useTrackPostHogEvent()
	const { account, subscription: loadedSubscription } = useAccountStore(
		useShallow((state) => ({
			account: state.account,
			subscription: state.subscription
		}))
	)
	const { protection, stale } = useProtectionContext()
	const subscription =
		loadedSubscription?.id === account?.billing?.subscription?.id
			? loadedSubscription
			: account?.billing?.subscription
	const status = subscription?.status || 'NONE'

	useEffect(() => {
		if (!account?.id || !userId || account.id === 'workspace-demo') return
		let current = true
		const accountId = account.id
		useAccountStore.setState({ subscriptionLoading: true })
		void ProductAccounts.getSubscriptionByAccountId(accountId)
			.then((subscription) => {
				if (
					current &&
					useAccountStore.getState().account?.id === accountId &&
					useUserStore.getState().user?.id === userId
				)
					useAccountStore.setState({ subscription, subscriptionLoading: false })
			})
			.catch(() => {
				if (
					current &&
					useAccountStore.getState().account?.id === accountId &&
					useUserStore.getState().user?.id === userId
				)
					useAccountStore.setState({ subscriptionLoading: false })
			})
		return () => {
			current = false
		}
	}, [account?.id, userId])

	if (status === 'CANCELLED') return null

	const isLegacy = account?.billing?.subscription?.plan?.tier === 'legacy'

	const currentValue = isLegacy
		? Number(account?.total_clicks || 0)
		: Number(protection?.reportedSpend ?? account?.reported_spend ?? 0)
	const limitValue = isLegacy
		? Number(account?.billing?.subscription?.plan?.clicks || 0)
		: protection?.effectiveSpendLimit ?? subscription?.plan.spend
	const isUnlimited =
		!isLegacy &&
		((subscription?.type === 'TRIAL' && !protection?.boostActive) ||
			!limitValue)
	const percentage = isUnlimited
		? 0
		: getProgressPercentage(currentValue, limitValue)
	const isWarning = !isUnlimited && percentage >= 90
	const remaining =
		isWarning && limitValue ? Math.max(0, limitValue - currentValue) : 0
	const spendColorClass = isUnlimited
		? 'cg-text-brand'
		: getSpendColorClass(percentage)
	const progressVariant = isUnlimited ? 'brand' : getProgressVariant(percentage)

	const usageLabel = isLegacy
		? 'Clicks'
		: isWarning
		? 'Monthly ad spend protection'
		: 'Monthly ad spend'

	const formatValue = (value: number) =>
		isLegacy ? value.toLocaleString() : formatUsageSpend(value)

	const showResetDate =
		status !== 'CANCELLING' && subscription?.type !== 'TRIAL'

	const resetDate = protection?.usagePeriodEnd
		? moment.utc(protection.usagePeriodEnd)
		: getNextSpendResetDate(
				subscription?.next_billing_date,
				subscription?.ends_at,
				subscription?.ended_at
		  )

	return (
		<>
			<div
				className="cg-flex cg-flex-col cg-gap-3 cg-bg-surface-2 cg-rounded-xl cg-p-3 cg-cursor-pointer cg-transition-colors hover:bg-t-step-inactive"
				onClick={() => navigate(`/settings/workspace/${account?.id}/billing`)}
			>
				<div className="cg-flex cg-items-center cg-justify-between">
					<span className="text-[14px] cg-font-medium cg-text-text-secondary">
						Usage
					</span>
					<ChevronRight
						className="cg-size-4 cg-text-text-tertiary"
						aria-hidden="true"
					/>
				</div>
				<div className="cg-flex cg-flex-col cg-gap-3">
					<div className="cg-flex cg-items-center cg-justify-between cg-gap-1">
						<span className="text-[12px] cg-text-text-primary">
							{usageLabel}
						</span>
						<div className="cg-flex cg-flex-wrap cg-items-center cg-justify-end cg-gap-1">
							<span
								className={`text-[12px] cg-font-semibold ${spendColorClass}`}
							>
								{stale ? 'Updating…' : formatValue(currentValue)}
							</span>
							<span
								className={`text-[14px] cg-font-normal ${
									isUnlimited ? 'cg-text-text-tertiary' : 'cg-text-brand-400'
								}`}
							>
								/
							</span>
							<span
								className={`text-[12px] cg-font-normal ${
									isUnlimited ? 'cg-text-text-tertiary' : 'cg-text-brand-400'
								}`}
							>
								{stale
									? '—'
									: isUnlimited
									? 'Unlimited'
									: formatValue(limitValue as number)}
							</span>
							{!stale && protection?.boostActive && protection.boostEndsAt && (
								<BoostTag endsAt={protection.boostEndsAt} />
							)}
						</div>
					</div>
					{!stale && !isUnlimited && (
						<ProgressBar
							value={percentage}
							size="sm"
							variant={progressVariant}
						/>
					)}
					{!stale && isWarning && (
						<div className="cg-flex cg-items-center cg-justify-between">
							<span className="text-[12px] cg-font-semibold cg-text-red-500">
								{formatValue(remaining)} remaining
							</span>
							<button
								className="text-[11px] cg-font-semibold cg-text-white cg-bg-red-500 cg-rounded-full cg-px-2.5 cg-py-1 hover:cg-bg-red-600 cg-transition-colors cg-cursor-pointer"
								onClick={(e) => {
									e.stopPropagation()
									trackPostHogEvent(
										PostHogEvent.UPGRADE_INTENT,
										PostHogPath.UPGRADE_PLAN_BANNER,
										null
									)
									onExtendProtection()
								}}
							>
								Extend protection
							</button>
						</div>
					)}
					{!stale && showResetDate && (
						<div className="cg-flex cg-items-center cg-gap-1.5">
							<StatusDot tone="brand" size="md" />
							<span className="text-[12px] cg-text-text-secondary">Resets</span>
							<span className="text-[12px] cg-text-text-primary">
								{resetDate.format('MMMM D, YYYY')}
							</span>
						</div>
					)}
				</div>
			</div>
		</>
	)
}
